/**
 * Что за объект чертежа под указателем — по зонам захвата слоёв холста.
 *
 * У каждой зоны захвата есть `data-role` и `data-id`; по ним ластик понимает, что удалять,
 * а комментарий — к чему привязаться. Подсвечивать при этом надо нарисованный
 * объект, а не его прозрачную зону: это группа зоны (у стены — сам многоугольник стены).
 */
import type { CommentTargetKind } from "@/shared/plan";
import { useProjectStore } from "@/shared/store";

/** Объект чертежа под указателем. */
export interface PlanObjectHit {
	/** `data-role` зоны захвата: wall, opening, item, fixture, cable, pipe, comment… */
	role: string;
	id: string;
	/** Что подсвечивать */
	element: SVGElement;
}

/** Объект, которому принадлежит элемент `target`; null — пустое место. */
export const planObjectAt = (target: EventTarget | null): PlanObjectHit | null => {
	const hit = target instanceof Element ? target.closest("[data-role][data-id]") : null;
	const role = hit?.getAttribute("data-role");
	const id = hit?.getAttribute("data-id");
	if (!hit || !role || !id) return null;
	const element = role === "wall" ? hit : (hit.parentElement ?? hit);
	if (!(element instanceof SVGElement)) return null;
	return { role, id, element };
};

/** К каким зонам захвата привязывается комментарий. Кабели, трубы и другие комментарии — нет. */
const COMMENT_TARGET_OF_ROLE: Record<string, CommentTargetKind> = {
	wall: "wall",
	opening: "opening",
	item: "item",
	fixture: "fixture",
};

/** Объект под комментарием: что за он и что подсвечивать. */
export interface CommentTargetHit {
	kind: CommentTargetKind;
	id: string;
	element: SVGElement;
}

/**
 * Объект под точкой экрана, к которому можно привязать комментарий; null — пустое место.
 * Смотрим всё, что под точкой, а не только верхний элемент: сверху бывает ручка самой
 * стрелки или рамка другого комментария.
 */
export const commentTargetUnder = (clientX: number, clientY: number): CommentTargetHit | null => {
	for (const element of document.elementsFromPoint(clientX, clientY)) {
		const hit = planObjectAt(element);
		let kind = hit ? COMMENT_TARGET_OF_ROLE[hit.role] : undefined;
		if (!hit || !kind) continue;
		// У светильника на потолке зона захвата тоже «fixture» — он лежит в items слоя электрики
		if (kind === "fixture" && hit.id in useProjectStore.getState().project.layers.electrical.items)
			kind = "light";
		return { kind, id: hit.id, element: hit.element };
	}
	return null;
};
